import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { format, differenceInDays, isValid } from 'date-fns';
import api from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Textarea } from '../components/common/Textarea';
import { Select } from '../components/common/Select';
import { Alert } from '../components/common/Alert';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

interface LeaveRequestForm {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  reason: string;
  emergencyContact: string;
  emergencyPhone: string;
  handoverNotes: string;
  substituteId?: string;
  areaOfResponsibility?: string;
}

export const EditLeaveRequest = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [workingDays, setWorkingDays] = useState(0);

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<LeaveRequestForm>();

  // Fetch existing leave request
  const { data: leaveRequest, isLoading: isLoadingRequest } = useQuery({
    queryKey: ['leaveRequest', id],
    queryFn: async () => {
      const response = await api.get(`/leaves/${id}`);
      console.log('Fetched leave request:', response.data);
      return response.data;
    }
  });

  // Fetch leave types
  const { data: leaveTypes } = useQuery({
    queryKey: ['leaveTypes'],
    queryFn: async () => {
      const response = await api.get('/leaves/types/all');
      return response.data || [];
    }
  });

  // Fetch leave balances
  const { data: balances } = useQuery({
    queryKey: ['leaveBalances'],
    queryFn: async () => {
      const response = await api.get('/leaves/balances');
      return response.data || [];
    }
  });

  // Fetch users for substitute selection
  const { data: users } = useQuery({
    queryKey: ['users-for-substitution'],
    queryFn: async () => {
      const response = await api.get('/users/for-substitution');
      return response.data.users || [];
    }
  });

  // Reset form when leave request is loaded
  useEffect(() => {
    if (leaveRequest) {
      const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return isValid(date) ? format(date, 'yyyy-MM-dd') : '';
      };

      reset({
        leaveTypeId: leaveRequest.leave_type_id?.toString() || '',
        startDate: formatDate(leaveRequest.start_date),
        endDate: formatDate(leaveRequest.end_date),
        reason: leaveRequest.reason || '',
        emergencyContact: leaveRequest.emergency_contact || '',
        emergencyPhone: leaveRequest.emergency_phone || '',
        handoverNotes: leaveRequest.handover_notes || '',
        substituteId: leaveRequest.substitute_id?.toString() || '',
        areaOfResponsibility: leaveRequest.area_of_responsibility || ''
      });
    }
  }, [leaveRequest, reset]);

  const startDate = watch('startDate');
  const endDate = watch('endDate');

  // Calculate working days
  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const days = differenceInDays(end, start) + 1;
      setWorkingDays(Math.max(0, days));
    }
  }, [startDate, endDate]);

  const updateRequestMutation = useMutation({
    mutationFn: async ({ data, status }: { data: LeaveRequestForm; status: string }) => {
      console.log('Updating leave request:', data, 'with status:', status);
      
      const response = await api.put(`/leaves/${id}`, {
        leave_type_id: data.leaveTypeId,
        start_date: data.startDate,
        end_date: data.endDate,
        reason: data.reason,
        emergency_contact: data.emergencyContact,
        emergency_phone: data.emergencyPhone,
        handover_notes: data.handoverNotes,
        substitute_id: data.substituteId,
        area_of_responsibility: data.areaOfResponsibility,
        status: status
      });
      
      console.log('Leave request updated:', response.data);
      
      return response.data;
    },
    onSuccess: (_, variables) => {
      showNotification({
        type: 'success',
        title: variables.status === 'pending' ? 'Request Submitted' : 'Draft Saved',
        message: variables.status === 'pending' 
          ? 'Your leave request has been submitted successfully.' 
          : 'Your draft has been saved successfully.'
      });
      navigate('/leaves');
    },
    onError: (error: any) => {
      console.error('Update error:', error);
    }
  });

  const onSubmit = (data: LeaveRequestForm, status: string = 'draft') => {
    if (updateRequestMutation.isPending) {
      return;
    }
    updateRequestMutation.mutate({ data, status });
  };

  const selectedLeaveType = watch('leaveTypeId');
  const balance = balances?.find((b: any) => b.leaveTypeId === Number(selectedLeaveType));

  if (isLoadingRequest) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!leaveRequest || leaveRequest.status !== 'draft') {
    return (
      <div className="flex justify-center items-center h-64">
        <Alert variant="error">
          Leave request not found or cannot be edited. Only draft requests can be edited.
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Edit Leave Request</h1>
        <p className="mt-1 text-sm text-gray-500">
          Update your leave request details
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Request Details</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {updateRequestMutation.isError && (
                  <Alert variant="error">
                    Failed to update leave request. Please try again.
                  </Alert>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Leave Type
                  </label>
                  <select
                    {...register('leaveTypeId', { required: 'Leave type is required' })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select leave type</option>
                    {leaveTypes?.map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {errors.leaveTypeId && (
                    <p className="mt-1 text-sm text-red-600">{errors.leaveTypeId.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Input
                      type="date"
                      label="Start Date"
                      {...register('startDate', { required: 'Start date is required' })}
                      error={errors.startDate?.message}
                    />
                  </div>
                  <div>
                    <Input
                      type="date"
                      label="End Date"
                      {...register('endDate', { required: 'End date is required' })}
                      error={errors.endDate?.message}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-sm font-medium text-gray-700">Duration: {workingDays} working days</p>
                  {balance && (
                    <p className="text-sm text-gray-600 mt-1">
                      Available balance: {balance.remaining} days
                    </p>
                  )}
                </div>

                <Textarea
                  label="Reason for Leave"
                  {...register('reason', { required: 'Reason is required' })}
                  error={errors.reason?.message}
                  rows={3}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Substitute (Optional)
                  </label>
                  <select
                    {...register('substituteId')}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select a substitute</option>
                    {users?.filter((u: any) => u.id !== user?.id).map((u: any) => (
                      <option key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                {watch('substituteId') && (
                  <Textarea
                    label="Area of Responsibility (for substitute)"
                    {...register('areaOfResponsibility')}
                    rows={2}
                  />
                )}

                <Textarea
                  label="Handover Notes (Optional)"
                  {...register('handoverNotes')}
                  rows={3}
                  placeholder="Any instructions or notes for your substitute or team..."
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Emergency Contact Name"
                    {...register('emergencyContact')}
                    error={errors.emergencyContact?.message}
                    placeholder="Optional - contact person name"
                  />
                  <Input
                    label="Emergency Phone"
                    {...register('emergencyPhone')}
                    error={errors.emergencyPhone?.message}
                    placeholder="Optional - contact phone number"
                  />
                </div>

                <div className="flex gap-4 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/leaves')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => handleSubmit((data) => onSubmit(data, 'draft'))()}
                    isLoading={updateRequestMutation.isPending}
                    disabled={updateRequestMutation.isPending}
                  >
                    Save as Draft
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleSubmit((data) => onSubmit(data, 'pending'))()}
                    isLoading={updateRequestMutation.isPending}
                    disabled={updateRequestMutation.isPending}
                  >
                    Submit Request
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Guidelines</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Submit your leave request at least 2 weeks in advance</li>
                <li>• Ensure you have sufficient leave balance</li>
                <li>• Provide detailed handover notes if necessary</li>
                <li>• Select a substitute for critical responsibilities</li>
                <li>• Include emergency contact information</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EditLeaveRequest;